<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import Modal from '../../../../components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { emitter } from 'shared/helpers/mitt';
import offerGroupsAPI from '../../../../api/offerGroups';

const { t } = useI18n();

const emit = defineEmits(['switch-view']);

const loading = ref(false);
const creating = ref(false);
const groups = ref([]);
const newName = ref('');
const showCreateModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const groupToDelete = ref(null);
const editGroupId = ref(null);
const editName = ref('');
const savingEdit = ref(false);

const fetchList = async () => {
  loading.value = true;
  try {
    const { data } = await offerGroupsAPI.list();
    groups.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
    groups.value = [];
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.PACKAGES.ERROR_LOAD'),
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const openCreateModal = () => {
  newName.value = '';
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  newName.value = '';
};

const createGroup = async () => {
  const name = newName.value.trim();
  if (!name) return;
  creating.value = true;
  try {
    const { data } = await offerGroupsAPI.create(name);
    groups.value = [...groups.value, data].sort((a, b) => a.name.localeCompare(b.name));
    closeCreateModal();
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.PACKAGES.SUCCESS_CREATED'),
      action: { type: 'success' },
    });
  } catch (error) {
    console.error(error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.PACKAGES.ERROR_CREATE'),
      action: { type: 'error' },
    });
  } finally {
    creating.value = false;
  }
};

const openEditModal = group => {
  editGroupId.value = group.id;
  editName.value = group.name;
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  editGroupId.value = null;
  editName.value = '';
};

const saveEdit = async () => {
  const name = editName.value.trim();
  if (!name || !editGroupId.value) return;
  savingEdit.value = true;
  try {
    const { data } = await offerGroupsAPI.update(editGroupId.value, name);
    const idx = groups.value.findIndex(g => g.id === data.id);
    if (idx !== -1) groups.value[idx] = data;
    groups.value = [...groups.value].sort((a, b) => a.name.localeCompare(b.name));
    closeEditModal();
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.PACKAGES.SUCCESS_UPDATED'),
      action: { type: 'success' },
    });
  } catch (error) {
    console.error(error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.PACKAGES.ERROR_UPDATE'),
      action: { type: 'error' },
    });
  } finally {
    savingEdit.value = false;
  }
};

const confirmDelete = group => {
  groupToDelete.value = group;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  if (!groupToDelete.value) return;
  try {
    loading.value = true;
    await offerGroupsAPI.destroy(groupToDelete.value.id);
    groups.value = groups.value.filter(g => g.id !== groupToDelete.value.id);
    showDeleteModal.value = false;
    groupToDelete.value = null;
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.PACKAGES.SUCCESS_DELETED'),
      action: { type: 'success' },
    });
  } catch (error) {
    console.error(error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.PACKAGES.ERROR_DELETE'),
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const handleBack = () => {
  emit('switch-view', 'offers');
};

onMounted(() => {
  fetchList();
});
</script>

<template>
  <div class="flex flex-col h-full bg-white dark:bg-slate-900 p-4">
    <header class="pb-4 mb-4">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-4 min-w-0">
          <button
            type="button"
            class="shrink-0 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            :aria-label="t('KANBAN.OFFERS.PACKAGES.BACK_ARIA')"
            @click="handleBack"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h1 class="text-base font-medium text-slate-900 dark:text-slate-100 truncate">
            {{ t('KANBAN.OFFERS.PACKAGES.TITLE') }}
          </h1>
        </div>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          class="shrink-0"
          @click="openCreateModal"
        >
          <template #icon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="shrink-0"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </template>
          {{ t('KANBAN.OFFERS.PACKAGES.ADD') }}
        </Button>
      </div>
    </header>

    <div class="flex-1 overflow-y-auto min-h-0 space-y-6">
      <div v-if="loading && !groups.length" class="flex justify-center py-12">
        <span class="loading-spinner" />
      </div>

      <div
        v-else-if="!groups.length"
        class="flex flex-col items-center justify-center py-12 text-slate-600 dark:text-slate-400"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="mb-3 opacity-50 text-slate-400 dark:text-slate-500 shrink-0"
          aria-hidden="true"
        >
          <rect width="20" height="5" x="2" y="3" rx="1" />
          <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
          <path d="M10 12h4" />
        </svg>
        <p class="text-sm mb-4">{{ t('KANBAN.OFFERS.PACKAGES.EMPTY') }}</p>
        <Button variant="outline" color="blue" size="sm" @click="openCreateModal">
          {{ t('KANBAN.OFFERS.PACKAGES.ADD') }}
        </Button>
      </div>

      <div
        v-else
        class="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
      >
        <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead class="bg-slate-50 dark:bg-slate-800/60">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ t('KANBAN.OFFERS.PACKAGES.NAME_COLUMN') }}
              </th>
              <th class="px-4 py-2 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ t('KANBAN.OFFERS.PACKAGES.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-800">
            <tr v-for="g in groups" :key="g.id" class="bg-white dark:bg-slate-900">
              <td class="px-4 py-3">
                <span class="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {{ g.name }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    @click="openEditModal(g)"
                  >
                    <template #icon>
                      <FluentIcon icon="edit" size="16" :icons="dashboardIcons" />
                    </template>
                  </Button>
                  <Button
                    variant="ghost"
                    color="ruby"
                    size="sm"
                    @click="confirmDelete(g)"
                  >
                    <template #icon>
                      <FluentIcon icon="delete" size="16" :icons="dashboardIcons" />
                    </template>
                  </Button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Modal
      v-model:show="showCreateModal"
      :on-close="closeCreateModal"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4 text-slate-900 dark:text-slate-100">
          {{ t('KANBAN.OFFERS.PACKAGES.CREATE_MODAL_TITLE') }}
        </h3>
        <label class="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
          {{ t('KANBAN.OFFERS.PACKAGES.NAME_COLUMN') }}
        </label>
        <input
          v-model="newName"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 mb-6"
          :placeholder="t('KANBAN.OFFERS.PACKAGES.NEW_NAME_PLACEHOLDER')"
          @keyup.enter="createGroup"
        />
        <div class="flex justify-end gap-2">
          <Button variant="ghost" color="slate" size="sm" @click="closeCreateModal">
            {{ t('KANBAN.OFFERS.MANAGER.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :isLoading="creating"
            @click="createGroup"
          >
            {{ t('KANBAN.OFFERS.PACKAGES.CREATE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model:show="showEditModal"
      :on-close="closeEditModal"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4 text-slate-900 dark:text-slate-100">
          {{ t('KANBAN.OFFERS.PACKAGES.EDIT_MODAL_TITLE') }}
        </h3>
        <label class="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
          {{ t('KANBAN.OFFERS.PACKAGES.NAME_COLUMN') }}
        </label>
        <input
          v-model="editName"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 mb-6"
          :placeholder="t('KANBAN.OFFERS.PACKAGES.NEW_NAME_PLACEHOLDER')"
          @keyup.enter="saveEdit"
        />
        <div class="flex justify-end gap-2">
          <Button variant="ghost" color="slate" size="sm" @click="closeEditModal">
            {{ t('KANBAN.OFFERS.MANAGER.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :isLoading="savingEdit"
            @click="saveEdit"
          >
            {{ t('KANBAN.OFFERS.PACKAGES.SAVE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4 text-slate-900 dark:text-slate-100">
          {{ t('KANBAN.OFFERS.PACKAGES.DELETE_TITLE') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mb-6">
          {{ t('KANBAN.OFFERS.PACKAGES.DELETE_MESSAGE') }}
          <span class="font-medium">"{{ groupToDelete?.name }}"</span>
        </p>
        <div class="flex justify-end gap-2">
          <Button variant="ghost" color="slate" size="sm" @click="showDeleteModal = false">
            {{ t('KANBAN.OFFERS.MANAGER.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="ruby"
            size="sm"
            :isLoading="loading"
            @click="handleDelete"
          >
            {{ t('KANBAN.OFFERS.PACKAGES.DELETE_CONFIRM') }}
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-light);
  border-top: 3px solid var(--color-woot);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
